import { toast } from 'sonner';
import { Icon } from '@iconify/react';
import { useFormStore } from '@/store';
import { useTranslation } from 'react-i18next';
import type { QuestionItemProps } from '@/types';
import React, { useState, useRef, useEffect } from 'react';
import { fileService } from '@/services/upload/file.service';
import { validateFile, getAllSupportedExtensions, getFileTypeFromExtension } from '@/utils/file-validation';
import { useServeFileById } from '@/hooks/upload/use-file';
import {
  ImagePreview,
  PdfPreview,
  DocumentPreview,
  SpreadsheetPreview,
  PresentationPreview,
  AudioPreview,
  VideoPreview
} from '../../form-answer/previews';

export const AddDocumentQuestion: React.FC<QuestionItemProps> = ({ question, questionIndex, sectionIndex, isMinimized }) => {
  const { t } = useTranslation();
  const { sections, updateSection } = useFormStore();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [documentName, setDocumentName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the hook to get file content for preview
  const { 
    data: fileUrl, 
    isLoading: isLoadingFile, 
    error: fileError 
  } = useServeFileById(documentName || '');

  // Initialize documentName from question.document
  useEffect(() => {
    if (question.document) {
      setDocumentName(question.document);
    }
  }, [question.document]);

  const uploadDocument = async (file: File) => {
    try {
      setIsUploading(true);
      
      // Validate file before upload
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.error || 'Invalid file');
        return;
      }
      
      const response = await fileService.uploadFile({ file, folder: 'form-design' });
      
      if (response.id) {
        const updatedQuestion = { 
          ...question, 
          document: response.id,
          documentType: validation.fileType?.type || 'document'
        };
        const updatedSection = { ...sections[sectionIndex], questions: [...sections[sectionIndex].questions] };
        updatedSection.questions[questionIndex] = updatedQuestion;
        updateSection(sectionIndex, updatedSection);
        toast.success('Document uploaded successfully!');
        setDocumentName(response.id);
      } else {
        toast.error('Uploading document failed');
      }
    } catch (error) {
      console.error('Upload error', error);
      toast.error('Uploading document failed');
    } finally {
      setIsUploading(false);
    }
  };

  const deleteDocument = async (fileId: string) => {
    try {
      setIsDeleting(true);
      const response = await fileService.deleteFile(fileId);
      
      if (response.success) {
        const updatedQuestion = { ...question, document: '' };
        const updatedSection = { ...sections[sectionIndex], questions: [...sections[sectionIndex].questions] };
        updatedSection.questions[questionIndex] = updatedQuestion;
        updateSection(sectionIndex, updatedSection);
        toast.success('Document deleted successfully!');
        setDocumentName(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast.error('Deleting document failed');
      }
    } catch (error) {
      console.error('Delete error', error);
      toast.error('Deleting document failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      uploadDocument(selectedFile);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteClick = () => {
    if (documentName) {
      deleteDocument(documentName);
    }
  };

  const renderFilePreview = () => {
    if (isLoadingFile) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <Icon icon="mdi:loading" className="text-4xl text-gray-400 animate-spin" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{t('forms.loadingDocument')}</p>
            </div>
          </div>
        </div>
      );
    }

    if (fileError || !fileUrl) {
      return (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
          <div className="flex items-center space-x-3">
            <Icon icon="mdi:file-alert" className="text-4xl text-red-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{t('forms.documentNotFound')}</p>
              <p className="text-xs text-gray-500">{t('forms.unableToLoadDocument')}</p>
            </div>
          </div>
        </div>
      );
    }

    const fileName = documentName || 'Unknown file';
    const documentType = question.documentType || getFileTypeFromExtension(fileName)?.type || 'document';
    const previewProps = { fileUrl, fileName };

    switch (documentType) {
      case 'image':
        return <ImagePreview {...previewProps} />;
      case 'pdf':
        return <PdfPreview {...previewProps} />;
      case 'document':
        return <DocumentPreview {...previewProps} />;
      case 'spreadsheet':
        return <SpreadsheetPreview {...previewProps} />;
      case 'presentation':
        return <PresentationPreview {...previewProps} />;
      case 'audio':
        return <AudioPreview {...previewProps} />;
      case 'video':
        return <VideoPreview {...previewProps} />;
      default:
        return (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/70">
            <div className="flex items-center space-x-3">
              <Icon icon="mdi:file" className="text-4xl text-gray-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate" title={fileName}>
                  {fileName}
                </p>
                <p className="text-xs text-gray-500">{t('forms.document')}</p>
              </div>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200 transition-colors"
              >
                {t('forms.download')}
              </a>
            </div>
          </div>
        );
    }
  };

  return (
    !isMinimized && (
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
          <button
            onClick={handleButtonClick}
            className="px-4 py-2 rounded bg-[#F1F1F2] font-sm text-sm text-[#000B22]"
            style={{ color: isUploading || isDeleting ? 'gray' : 'darkBlue' }}
            disabled={isUploading || isDeleting || !!documentName}
            type="button"
          >
            {!isUploading ? <p>{t('questionDesign.chooseFile')}</p> : <p>{t('questionDesign.uploading')}</p>}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={getAllSupportedExtensions()}
            className="hidden"
            onChange={handleChange}
            disabled={isUploading || isDeleting}
          />
          {documentName && (
            <div className="flex items-center space-x-2 min-w-0">
              <button
                onClick={handleDeleteClick}
                className="text-[#FF7C72] flex-shrink-0"
                disabled={isDeleting}
                type="button"
                title="Remove document"
              >
                <Icon icon="mdi:bin" className="text-lg" />
              </button>
            </div>
          )}
        </div>
        
        {/* Show file preview when document is uploaded */}
        {documentName && (
          <div className="mt-4">
            {renderFilePreview()}
          </div>
        )}
      </div>
    )
  );
};